import { ref, computed, onMounted } from 'vue'
import { useTheme } from './useTheme'

export function useGlassmorphism() {
  const { theme } = useTheme()
  
  // backdrop-filterのサポート検出
  const supportsBackdropFilter = ref(false)
  
  // ブラー強度（ピクセル）
  const blurIntensity = ref(10)
  
  // 透明度
  const opacity = ref(0.85)
  
  // テーマに応じたブラー強度を計算
  const themeBlurIntensity = computed(() => {
    // ダークモードの場合は少し強めのブラーを適用
    return theme.value === 'dark' ? blurIntensity.value + 2 : blurIntensity.value
  })
  
  // テーマに応じた透明度を計算
  const themeOpacity = computed(() => {
    // ダークモードの場合は少し透明度を下げる
    return theme.value === 'dark' ? opacity.value - 0.05 : opacity.value
  })
  
  // backdrop-filterのサポートチェック
  const checkBackdropFilterSupport = () => {
    if (typeof CSS !== 'undefined' && CSS.supports) {
      supportsBackdropFilter.value = CSS.supports('backdrop-filter', 'blur(10px)') ||
                                      CSS.supports('-webkit-backdrop-filter', 'blur(10px)')
    } else {
      // 古いブラウザ用のフォールバック検出
      const testElement = document.createElement('div')
      testElement.style.webkitBackdropFilter = 'blur(10px)'
      testElement.style.backdropFilter = 'blur(10px)'
      supportsBackdropFilter.value = testElement.style.backdropFilter !== '' ||
                                      testElement.style.webkitBackdropFilter !== ''
    }
  }
  
  // glassmorphismスタイルの生成
  const glassmorphismStyle = computed(() => {
    const baseStyle = {
      background: `var(--navbar-gradient)`,
      boxShadow: `var(--navbar-shadow)`,
      borderBottom: `var(--navbar-border-bottom)`,
      transition: `var(--navbar-item-transition)`
    }
    
    if (supportsBackdropFilter.value) {
      // backdrop-filterをサポートしている場合
      return {
        ...baseStyle,
        backdropFilter: `blur(${themeBlurIntensity.value}px)`,
        webkitBackdropFilter: `blur(${themeBlurIntensity.value}px)`,
        backgroundColor: theme.value === 'dark' 
          ? `rgba(26, 26, 26, ${themeOpacity.value})`
          : `rgba(255, 255, 255, ${themeOpacity.value})`
      }
    } else {
      // フォールバック: 半透明の背景とbox-shadow
      return {
        ...baseStyle,
        backgroundColor: theme.value === 'dark' 
          ? `rgba(26, 26, 26, 0.95)`
          : `rgba(255, 255, 255, 0.95)`,
        boxShadow: theme.value === 'dark'
          ? `0 2px 20px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.1)`
          : `0 2px 20px rgba(0, 0, 0, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.8)`
      }
    }
  })
  
  // スクロール時のglassmorphismスタイル
  const scrolledGlassmorphismStyle = computed(() => {
    const baseStyle = glassmorphismStyle.value
    
    if (supportsBackdropFilter.value) {
      return {
        ...baseStyle,
        backdropFilter: `blur(${themeBlurIntensity.value + 2}px)`,
        webkitBackdropFilter: `blur(${themeBlurIntensity.value + 2}px)`,
        backgroundColor: theme.value === 'dark'
          ? `rgba(26, 26, 26, 0.96)`
          : `rgba(255, 255, 255, 0.98)`
      }
    } else {
      return {
        ...baseStyle,
        backgroundColor: theme.value === 'dark'
          ? `rgba(26, 26, 26, 0.98)`
          : `rgba(255, 255, 255, 0.99)`,
        boxShadow: theme.value === 'dark'
          ? `0 2px 25px rgba(0, 0, 0, 0.35), inset 0 1px 0 rgba(255, 255, 255, 0.1)`
          : `0 2px 25px rgba(0, 0, 0, 0.12), inset 0 1px 0 rgba(255, 255, 255, 0.8)`
      }
    }
  })
  
  // モバイルオーバーレイスタイル
  const mobileOverlayStyle = computed(() => {
    return {
      backgroundColor: `var(--navbar-mobile-overlay)`,
      backdropFilter: supportsBackdropFilter.value ? 'blur(4px)' : 'none',
      webkitBackdropFilter: supportsBackdropFilter.value ? 'blur(4px)' : 'none'
    }
  })
  
  // ドロップダウンのglassmorphismスタイル
  const dropdownGlassmorphismStyle = computed(() => {
    const baseStyle = {
      boxShadow: `var(--navbar-dropdown-shadow)`,
      border: `1px solid ${theme.value === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)'}`,
      borderRadius: '12px',
      overflow: 'hidden'
    }
    
    if (supportsBackdropFilter.value) {
      return {
        ...baseStyle,
        backgroundColor: theme.value === 'dark'
          ? `rgba(42, 42, 42, 0.9)`
          : `rgba(255, 255, 255, 0.9)`,
        backdropFilter: `blur(${themeBlurIntensity.value}px)`,
        webkitBackdropFilter: `blur(${themeBlurIntensity.value}px)`
      }
    } else {
      return {
        ...baseStyle,
        backgroundColor: theme.value === 'dark'
          ? `rgba(42, 42, 42, 0.98)`
          : `rgba(255, 255, 255, 0.98)`
      }
    }
  })
  
  // prefers-reduced-motionのチェック
  const prefersReducedMotion = computed(() => {
    if (typeof window !== 'undefined' && window.matchMedia) {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches
    }
    return false
  })
  
  // アニメーション用のトランジション設定
  const transitionStyle = computed(() => {
    if (prefersReducedMotion.value) {
      return 'none'
    }
    return 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
  })
  
  // glassmorphism効果を適用するヘルパー関数
  const applyGlassmorphism = (element, isScrolled = false) => {
    if (!element) return
    
    const style = isScrolled ? scrolledGlassmorphismStyle.value : glassmorphismStyle.value
    
    Object.entries(style).forEach(([property, value]) => {
      element.style[property] = value
    })
  }
  
  // glassmorphism効果をリセットするヘルパー関数
  const resetGlassmorphism = (element) => {
    if (!element) return
    
    element.style.backdropFilter = ''
    element.style.webkitBackdropFilter = ''
    element.style.backgroundColor = ''
    element.style.boxShadow = ''
    element.style.border = ''
  }
  
  // ライフサイクルフック
  onMounted(() => {
    checkBackdropFilterSupport()
  })
  
  return {
    supportsBackdropFilter,
    blurIntensity,
    opacity,
    themeBlurIntensity,
    glassmorphismStyle,
    scrolledGlassmorphismStyle,
    mobileOverlayStyle,
    dropdownGlassmorphismStyle,
    prefersReducedMotion,
    transitionStyle,
    applyGlassmorphism,
    resetGlassmorphism
  }
}